import React, { useState } from 'react';
import { Cliente, updateCliente } from '../services/clienteService';
import { Button, Checkbox, FormField, Input, Modal, Select } from './ui';

interface ClienteFormProps {
  cliente: Cliente;
  onClienteSaved: () => void;
  onCancel: () => void;
}

const ClienteForm: React.FC<ClienteFormProps> = ({ cliente, onClienteSaved, onCancel }) => {
  const [formData, setFormData] = useState<Cliente>({
    id: cliente.id,
    nombre: cliente.nombre,
    razon_social: cliente.razon_social,
    cuit: cliente.cuit,
    telefono: cliente.telefono,
    direccion: cliente.direccion,
    ciudad: cliente.ciudad,
    provincia: cliente.provincia,
    email: cliente.email,
    estado: cliente.estado,
    p_busqueda: cliente.p_busqueda,
    p_carga: cliente.p_carga,
    p_modif_analisis: cliente.p_modif_analisis,
    p_nuevo_analisis: cliente.p_nuevo_analisis
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    if (type === 'number') {
      setFormData({ ...formData, [name]: parseInt(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.id) return;

    setLoading(true);
    setError(null);

    try {
      await updateCliente(formData.id, formData);
      onClienteSaved();
    } catch (err) {
      console.error('Error al actualizar cliente:', err);
      setError('Error al actualizar el cliente. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Editar Cliente"
      onClose={onCancel}
      size="lg"
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="cliente-form" loading={loading}>
            Guardar
          </Button>
        </div>
      }
    >
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form id="cliente-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Nombre" htmlFor="nombre" required>
            <Input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField label="Razón Social" htmlFor="razon_social" required>
            <Input
              type="text"
              id="razon_social"
              name="razon_social"
              value={formData.razon_social}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField label="CUIT" htmlFor="cuit">
            <Input
              type="text"
              id="cuit"
              name="cuit"
              value={formData.cuit || ''}
              onChange={handleChange}
            />
          </FormField>

          <FormField label="Email" htmlFor="email">
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email || ''}
              onChange={handleChange}
            />
          </FormField>

          <FormField label="Teléfono" htmlFor="telefono">
            <Input
              type="text"
              id="telefono"
              name="telefono"
              value={formData.telefono || ''}
              onChange={handleChange}
            />
          </FormField>

          {/* La API entrega y recibe el estado con la misma convención en todas
              las pantallas: 0 es Activo y 1 es Inactivo. */}
          <FormField label="Estado" htmlFor="estado">
            <Select
              id="estado"
              name="estado"
              value={Number(formData.estado) === 1 ? '1' : '0'}
              onChange={(e) => setFormData({ ...formData, estado: Number(e.target.value) })}
            >
              <option value="0">Activo</option>
              <option value="1">Inactivo</option>
            </Select>
          </FormField>
        </div>

        <FormField label="Dirección" htmlFor="direccion">
          <Input
            type="text"
            id="direccion"
            name="direccion"
            value={formData.direccion || ''}
            onChange={handleChange}
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Ciudad" htmlFor="ciudad">
            <Input
              type="text"
              id="ciudad"
              name="ciudad"
              value={formData.ciudad || ''}
              onChange={handleChange}
            />
          </FormField>

          <FormField label="Provincia" htmlFor="provincia">
            <Input
              type="text"
              id="provincia"
              name="provincia"
              value={formData.provincia || ''}
              onChange={handleChange}
            />
          </FormField>
        </div>

        {/* Sección de Permisos */}
        <div className="border-t border-slate-200 pt-4">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Permisos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="Búsqueda"
              id="p_busqueda"
              name="p_busqueda"
              checked={formData.p_busqueda || false}
              onChange={(e) => setFormData({ ...formData, p_busqueda: e.target.checked })}
            />

            <Checkbox
              label="Carga"
              id="p_carga"
              name="p_carga"
              checked={formData.p_carga || false}
              onChange={(e) => setFormData({ ...formData, p_carga: e.target.checked })}
            />

            <Checkbox
              label="Modificar Análisis"
              id="p_modif_analisis"
              name="p_modif_analisis"
              checked={formData.p_modif_analisis || false}
              onChange={(e) => setFormData({ ...formData, p_modif_analisis: e.target.checked })}
            />

            <Checkbox
              label="Nuevo Análisis"
              id="p_nuevo_analisis"
              name="p_nuevo_analisis"
              checked={formData.p_nuevo_analisis || false}
              onChange={(e) => setFormData({ ...formData, p_nuevo_analisis: e.target.checked })}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ClienteForm;
